import * as Yup from 'yup';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useForm,
  FieldData,
  FormikFieldData,
  BrandingProductType,
  TextField,
  Tooltip,
  Box,
  withStyles,
  BodyText,
  ErrorText,
} from 'vopay';
import { Checkbox } from '#shared/components/UI';
import BrandingLogoField from './BrandingLogoField';
import styles from './BrandingSettingsForm.module.scss';

const cn = withStyles(styles);

// Small Logo and Secondary Colour belong to the products that render them;
// every other product only shows them once "Apply to all products" is ticked.
export function showsPortalFields(productType: BrandingProductType) {
  return productType === BrandingProductType.Portal || productType === BrandingProductType.Onboarding;
}

// Required-looking in both edit and read-only states . The library's
// asterisk only shows when inputState === 'required', but isReviewing forces every field
// to 'disabled' — unreachable here since this form toggles isReviewing. Drawn manually instead.
function requiredLabel(text: string) {
  return (
    <Box ai="center" gapSize="2px">
      <span>{text}</span>
      <span style={{ color: 'red' }}>*</span>
    </Box>
  );
}

// Same bordered box in both states — TextField's own outlined chrome
// doesn't match it, so only the value swaps to a bare (variant="none") TextField when editing.
function renderColorField(isReviewing: boolean, fieldData: FormikFieldData<string>) {
  const { value, errorMsg, ...rest } = fieldData;

  return (
    <div className={cn('fieldWrapper')}>
      <div className={cn('fieldBox', 'colorFieldBox')}>
        <span className={cn('colorSwatch')} style={{ background: value }} />

        {isReviewing ? (
          <span className={cn('colorFieldStaticValue')}>{value}</span>
        ) : (
          <TextField
            preset="text"
            variant="none"
            value={value}
            padding="0"
            containerWidth="100%"
            inputClass={cn('colorFieldInput')}
            {...rest}
            error={null}
          />
        )}
      </div>

      <ErrorText error={errorMsg} size="sm" />
    </div>
  );
}

export type BrandingValues = {
  Logo: string;
  SmallLogo: string;
  PrimaryColor: string;
  SecondaryColor: string;
  ApplyToAll: boolean;
};

const hexColorRegex = /^#[0-9a-fA-F]{6}$/;

type UseBrandingSettingsFormConfigArgs = {
  productType: BrandingProductType;
  initialValues: BrandingValues;
  isEditing: boolean;
  onSubmit: (values: BrandingValues) => void | Promise<void>;
};

export default function useBrandingSettingsFormConfig({
  productType,
  initialValues,
  isEditing,
  onSubmit,
}: UseBrandingSettingsFormConfigArgs) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Branding.Form' });

  const config = useMemo<FieldData<BrandingValues>[]>(
    () => [
      {
        fieldName: 'Logo',
        label: () => requiredLabel(t('logoLabel', { defaultValue: 'Logo' })),
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.string().required(t('requiredValidation', { defaultValue: 'This field is required' })),
        renderField: ({ getFormikData }) => {
          const { value, name, onBlur, onChange, disabled, errorMsg } = getFormikData<string>({});

          return (
            <BrandingLogoField
              name={name}
              value={value}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
              error={errorMsg}
            />
          );
        },
      },
      {
        fieldName: 'SmallLogo',
        label: () => t('smallLogoLabel', { defaultValue: 'Logo (Small)' }),
        tooltip: () => (
          <Tooltip
            information={
              <BodyText>
                {t('smallLogoTooltip', {
                  defaultValue: 'Used where space is limited, like mobile headers and smaller screens.',
                })}
              </BodyText>
            }
            position="top"
          />
        ),
        getInputState: () => 'optional',
        // Ticking "Apply to all products" reveals them so eLinx/Email can set what gets saved to Portal too.
        showField: (values) => showsPortalFields(productType) || values.ApplyToAll,
        renderField: ({ getFormikData }) => {
          const { value, name, onBlur, onChange, disabled, errorMsg } = getFormikData<string>({});

          return (
            <BrandingLogoField
              name={name}
              value={value}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
              error={errorMsg}
            />
          );
        },
      },
      {
        fieldName: 'PrimaryColor',
        label: () => requiredLabel(t('primaryColorLabel', { defaultValue: 'Primary Colour' })),
        getInputState: () => 'optional',
        showField: () => true,
        validate: () =>
          Yup.string()
            .matches(hexColorRegex, t('invalidHexColor', { defaultValue: 'HEX color is invalid.' }))
            .required(t('requiredValidation', { defaultValue: 'This field is required' })),
        renderField: ({ getFormikData }) => renderColorField(!isEditing, getFormikData<string>({})),
      },
      {
        fieldName: 'SecondaryColor',
        label: () => requiredLabel(t('secondaryColorLabel', { defaultValue: 'Secondary Colour' })),
        tooltip: () => (
          <Tooltip
            information={
              <BodyText>
                {t('secondaryColorTooltip', {
                  defaultValue: 'Used for accents like buttons and highlights.',
                })}
              </BodyText>
            }
            position="top"
          />
        ),
        getInputState: () => 'optional',
        showField: (values) => showsPortalFields(productType) || values.ApplyToAll,
        validate: () =>
          Yup.string()
            .matches(hexColorRegex, t('invalidHexColor', { defaultValue: 'HEX color is invalid.' }))
            .required(t('requiredValidation', { defaultValue: 'This field is required' })),
        renderField: ({ getFormikData }) => renderColorField(!isEditing, getFormikData<string>({})),
      },
      {
        fieldName: 'ApplyToAll',
        getInputState: () => 'optional',
        showField: () => isEditing,
        renderField: ({ ctx, getFormikData }) => {
          const { value, name, disabled } = getFormikData<boolean>({});

          return (
            <Checkbox
              name={name}
              checked={value}
              disabled={disabled}
              label={t('applyToAllLabel', { defaultValue: 'Apply to all products' })}
              onChange={(checked) => ctx.setFieldValue(name, checked)}
            />
          );
        },
      },
    ],

    [productType, isEditing, t],
  );

  const { ctx, renderFormElements } = useForm<BrandingValues>({
    config,
    initialValues,
    enableReinitialize: true,
    isReviewing: !isEditing,
    onSubmit,
  });

  const logoFields = useMemo(
    () => config.filter((field) => field.fieldName === 'Logo' || field.fieldName === 'SmallLogo'),
    [config],
  );

  const colorFields = useMemo(
    () => config.filter((field) => field.fieldName === 'PrimaryColor' || field.fieldName === 'SecondaryColor'),
    [config],
  );

  const applyToAllField = useMemo(() => config.filter((field) => field.fieldName === 'ApplyToAll'), [config]);

  return { ctx, renderFormElements, logoFields, colorFields, applyToAllField };
}
